import React, { useState, useEffect, useCallback } from 'react'
import { Form, Card, Row, Col, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import axios from 'axios'
import { BsFunnelFill, BsSearch, BsArrowClockwise, BsCalendar3, BsCheckCircleFill, BsXCircleFill, BsCheckLg } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useParams } from 'react-router-dom'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import TablePagination from './commonComponents/TablePagination'

const ALLOWED_STATUSES = ['present', 'absent', 'leave']

const getSessionName = () =>
  Cookies.get('session_name') || Cookies.get('session') || ''

const getHolidayListFromResponse = (responseData) => {
  if (Array.isArray(responseData?.data)) return responseData.data
  if (responseData?.data && typeof responseData.data === 'object') {
    return [responseData.data]
  }
  if (Array.isArray(responseData?.holidays)) return responseData.holidays
  if (Array.isArray(responseData?.holiday)) return responseData.holiday
  if (responseData?.holiday && typeof responseData.holiday === 'object') {
    return [responseData.holiday]
  }
  return []
}

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const normalizeApiDate = (date) => {
  if (!date) return ''
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10)
  }
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) {
    return String(date).split('T')[0] || ''
  }
  return getLocalDateString(parsed)
}

const formatDisplayDate = (date) => {
  if (!date) return ''
  const parsed = new Date(`${normalizeApiDate(date)}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const pickRollNumber = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

const compareRollNumbers = (rollA, rollB) => {
  const a = pickRollNumber(rollA)
  const b = pickRollNumber(rollB)
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

const sortStudentsByRollNumber = (students = []) =>
  [...students].sort((left, right) =>
    compareRollNumbers(
      pickRollNumber(left.roll_number, left.roll_no, left.rollNumber),
      pickRollNumber(right.roll_number, right.roll_no, right.rollNumber),
    ),
  )

const fetchEnrollmentRollNumber = async (studentId, classId, authToken) => {
  if (!studentId || !classId) return ''
  try {
    const response = await axios.get(BASE_URL + '/get/student/all/enroll', {
      params: { student_id: studentId },
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    })
    const enrollments = Array.isArray(response?.data?.data)
      ? response.data.data
      : []
    const classMatch = enrollments.find(
      (row) => Number(row.class_id) === Number(classId),
    )
    const fallback = enrollments[0]
    return pickRollNumber(
      classMatch?.roll_number,
      classMatch?.roll_no,
      fallback?.roll_number,
      fallback?.roll_no,
    )
  } catch {
    return ''
  }
}

const enrichStudentsWithRollNumbers = async (students, classId, authToken) => {
  if (!Array.isArray(students) || students.length === 0) return []
  const enriched = await Promise.all(
    students.map(async (student) => {
      const existing = pickRollNumber(
        student.roll_number,
        student.roll_no,
        student.rollNumber,
      )
      if (existing) return { ...student, roll_number: existing }
      const rollNumber = await fetchEnrollmentRollNumber(
        student.id,
        classId,
        authToken,
      )
      return { ...student, roll_number: rollNumber }
    }),
  )
  return sortStudentsByRollNumber(enriched)
}

const mapStudentToRow = (student, existingAtt = null) => ({
  ...student,
  roll_number: pickRollNumber(
    student.roll_number,
    student.roll_no,
    student.rollNumber,
  ),
  status: existingAtt?.status || '',
  remarks: existingAtt?.remarks || '',
  attendance_id: existingAtt?.id ?? null,
  alreadyTaken: Boolean(existingAtt),
})

const DataTable = () => {
  const {token, logout} = useAuth()
  const { isCollapsed } = useSidebar()
  const institute_id = Cookies.get('institute_id')
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])
  const { student_id, course_id, class_id } = useParams()
  console.log(
    'student_id: ',
    student_id,
    'course_id: ',
    course_id,
    'class_id: ',
    class_id,
  )

  const [selectedClass, setSelectedClass] = useState('')
  // const [selectedTeachers, setSelectedTeachers] = useState('')
  const [openTable, setOpenTable] = useState(false)

  const [classes, setClasses] = useState([])
  // const [teachers, setTeachers] = useState([])
  const [students, setStudents] = useState([])
  const [selectedDate, setSelectedDate] = useState(getLocalDateString())

  const [data, setData] = useState([])
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [resetKey, setResetKey] = useState(0)
  const [courses, setCourses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [filteredStudents, setFilteredStudents] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [loadingAttendance, setLoadingAttendance] = useState(false)
  const [checkingHoliday, setCheckingHoliday] = useState(false)
  const [tableMessage, setTableMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [classStudents, setClassStudents] = useState([])
  useEffect(() => {
    ApiEndpoints.getAllClasses().then((res) => {
      console.log('Classes:', res.data.data)
      setClasses(res.data.data)
    })
  }, [])

  useEffect(() => {
    const userCookie = Cookies.get('user')
    let token = null
    let isTeacher = false
    if (userCookie) {
      try {
        const parsed = JSON.parse(userCookie)
        token = parsed?.token || null
        const userType = String(parsed?.user_type || '').toLowerCase()
        const roles = Array.isArray(parsed?.roles)
          ? parsed.roles.map((r) => String(r).toLowerCase())
          : []
        isTeacher = userType === 'teacher' || roles.includes('teacher')
      } catch (error) {
        console.error('Failed to parse user cookie', error)
      }
    }

    if (isTeacher) {
      axios
        .get(BASE_URL + '/teacher-classes', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        })
        .then((res) => {
          const raw = res.data?.data ?? res.data
          const list = Array.isArray(raw)
            ? raw
            : raw?.assigned_classes ?? raw?.assignedClasses ?? raw?.classes ?? []
          const coursesList = []
          const seen = new Set()
          ;(Array.isArray(list) ? list : []).forEach((item) => {
            const id = item?.course_id ?? item?.courseId ?? item?.course?.id
            const name =
              item?.course_name ??
              item?.courseName ??
              (typeof item?.course === 'string' ? item.course : item?.course?.name)
            if (id == null || id === '' || !name) return
            const key = String(id)
            if (seen.has(key)) return
            seen.add(key)
            coursesList.push({ id, name })
          })
          console.log('Courses', coursesList)
          setCourses(coursesList)
        })
        .catch(console.error)
      return
    }

    ApiEndpoints.getAllCourses()
      .then((res) => {
        console.log('Courses', res.data.data)
        setCourses(res.data.data)
      })
      .catch(console.error)
  }, [])

  useEffect(() => {
    if (selectedCourseId) {
      ApiEndpoints.getAllClassesByCourseId(selectedCourseId)
        .then((res) => {
          const { data } = res
          if (data.status === false || !data.data?.length) {
            setClasses([])
          } else {
            setClasses(data.data)
            // setSelectedClass(classId)
          }
        })
        .catch(() => {
          setClasses([])
        })
    }
  }, [selectedCourseId])
  useEffect(() => {
    if (course_id) {
      handleCourseChange({ target: { value: course_id } })
    }
  }, [course_id])

  useEffect(() => {
    const institueId = Cookies.get('institute_id')
    const userCookie = Cookies.get('user')

    let token = null
    if (userCookie) {
      try {
        const user = JSON.parse(userCookie)
        token = user.token
        console.log('TOKEN:', token)
      } catch (error) {
        console.error('Failed to parse user cookie', error)
      }
    }
    const fetchFilteredData = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'institute-Id': institueId || institute_id,
        }

        const response = await axios.get(
          BASE_URL+'/student/all',
          { headers },
        )
        const students = response.data.data

        const student =
          students.find((st) => Number(st.id) === Number(student_id)) || 'N/A'
        console.log('Select Student data:', student)

        const studentParId = students.find(
          (st) => Number(st.id) === Number(student_id),
        )
        setFilteredStudents(studentParId)
        // setStudents(students)
        // setSelectedStudent(student)
        // setStudents(response.data.data)
      } catch (error) {
        console.error(error.response?.data?.message || 'Something wents wrong.')
      }
    }
    fetchFilteredData()
  }, [])

  useEffect(() => {
    if (course_id || class_id || student_id) {
      const selectedValues = {
        course_id: course_id,
        class_id: class_id,
        student_id: student_id,
      }
      setSelectedData(selectedValues)
      setOpenTable(true)
      fetchFilteredData(selectedValues)
    }
  }, [class_id, course_id, student_id])
  const handleStatusChange = (studentId, newStatus) => {
    setData((prevData) =>
      prevData.map((student) =>
        student.id === studentId && !student.alreadyTaken
          ? { ...student, status: newStatus }
          : student,
      ),
    )
    console.log(`🟢 Student ID: ${studentId}, New Status: ${newStatus}`)
  }

  const handleRemarksChange = (studentId, newRemarks) => {
    setData((prevStudents) =>
      prevStudents.map((student) =>
        student.id === studentId && !student.alreadyTaken
          ? { ...student, remarks: newRemarks }
          : student,
      ),
    )
    console.log(`📝 Student ID: ${studentId}, New Remarks: ${newRemarks}`)
  }

  // const filteredData = students.filter((item) =>
  //   Object.values(item).some(
  //     (value) =>
  //       value &&
  //       typeof value === 'string' &&
  //       value.toLowerCase().includes(search.toLowerCase()),
  //   ),
  // )

  // const searchedData = filteredData.filter(
  //   (student) =>
  //     student.studentName &&
  //     student.studentName.toLowerCase().includes(search.toLowerCase()),
  // )

  // const totalPages = Math.ceil(searchedData.length / itemsPerPage)
  // const currentItems = searchedData.slice(
  //   (currentPage - 1) * itemsPerPage,
  //   currentPage * itemsPerPage,
  // )

  // useEffect(() => {
  //   let filteredStu = students
  //   console.log('🔵 Filtered Students:', filteredData)

  //   if (selectedClass) {
  //     const instituteId = Cookies.get('institute_id')

  //     const headers = {
  //       'institute-Id': Number(instituteId),
  //     }

  //     axios
  //       .get(
  //         BASE_URL+`/get/student/by-class?class_id=${selectedClass}`,
  //         { headers },
  //       )
  //       // .then((res) => {
  //       //   const classStudents = res.data.data || []
  //       //   console.log('Fetched Students by class:', classStudents)

  //       //   // const classStudentIds = new Set(classStudents.map((stu) => stu.id))
  //       //   // const filteredData = filteredStu.filter((student) =>
  //       //   //   classStudentIds.has(student.id),
  //       //   // )
  //       //   // console.log(
  //       //   //   '✅ Filtered Data to Display classStudentIds:',
  //       //   //   classStudentIds,
  //       //   // )

  //       //   // console.log('✅ Filtered Data to Display filteredData:', filteredData)

  //       //   setData(classStudents) // for table
  //       // })
  //       .then((res) => {
  //         console.log('Students after class select:', res.data.data)
  //         setFilteredStudents(res.data.data)
  //       })
  //       .catch((error) => {
  //         // This is where 404 or other errors come
  //         if (
  //           error.response?.data?.message === 'No classes found for this course'
  //         ) {
  //           console.error('No classes found for this course.')
  //         } else {
  //           console.error('Error fetching classes:', error)
  //           console.error('Something went wrong while fetching classes.')
  //         }
  //         setFilteredStudents([])
  //         setClasses([])
  //       })
  //   }
  // }, [students, selectedClass])

  const mergeStudentsWithAttendance = useCallback((studentList, attendanceList) => {
    const byStudentId = {}
    ;(attendanceList || []).forEach((att) => {
      byStudentId[Number(att.student_id)] = att
    })
    return (studentList || []).map((student) =>
      mapStudentToRow(student, byStudentId[Number(student.id)] || null),
    )
  }, [])

  const isHolidayForStudentAttendance = useCallback(async (date) => {
    setTableMessage('')
    const session = getSessionName()
    if (!session) {
      setTableMessage('Session not found. Please select institute again.')
      return true
    }

    setCheckingHoliday(true)
    try {
      const [dateRes, studentHolidayRes] = await Promise.all([
        ApiEndpoints.getHolidayByDate(session, { date }).catch((error) => ({
          __error: error,
        })),
        ApiEndpoints.getHolidayList(session, 'STUDENT').catch((error) => ({
          __error: error,
        })),
      ])

      if (dateRes?.__error && studentHolidayRes?.__error) {
        setTableMessage(
          dateRes.__error.response?.data?.message ||
            studentHolidayRes.__error.response?.data?.message ||
            'Failed to check holiday for date.',
        )
        return true
      }

      const normalizedDate = normalizeApiDate(date)
      const isStudentApplicableHoliday = (holiday) => {
        const holidayType = String(
          holiday?.holiday_type || holiday?.type || '',
        ).toUpperCase()
        return (
          !holidayType || holidayType === 'ALL' || holidayType === 'STUDENT'
        )
      }

      const dateHolidays = dateRes?.__error
        ? []
        : getHolidayListFromResponse(dateRes?.data).filter(
            isStudentApplicableHoliday,
          )

      const studentListHolidays = studentHolidayRes?.__error
        ? []
        : getHolidayListFromResponse(studentHolidayRes?.data).filter(
            (holiday) =>
              normalizeApiDate(holiday?.date) === normalizedDate &&
              isStudentApplicableHoliday(holiday),
          )

      const applicableHoliday = dateHolidays[0] || studentListHolidays[0]

      if (applicableHoliday) {
        const instituteTypeLabel =
          Cookies.get('institute_type') || 'Institute'
        const instituteTypeCapital = instituteTypeLabel.charAt(0).toUpperCase() + instituteTypeLabel.slice(1).toLocaleLowerCase();
        setTableMessage(
          applicableHoliday.title
            ? `${instituteTypeCapital} is on holiday in selected date: ${applicableHoliday.title}.`
            : `${instituteTypeCapital} is on holiday in selected date.`,
        )
        return true
      }

      return false
    } catch (error) {
      setTableMessage(
        error.response?.data?.message || 'Failed to check holiday for date.',
      )
      return true
    } finally {
      setCheckingHoliday(false)
    }
  }, [])

  const loadAttendanceForDate = useCallback(
    async (studentList, classId, date, studentIdFilter = null) => {
      if (!classId || !date || !studentList?.length) {
        setData([])
        return
      }

      const isHoliday = await isHolidayForStudentAttendance(date)
      if (isHoliday) {
        setData([])
        setOpenTable(true)
        return
      }

      setLoadingAttendance(true)
      try {
        const payload = {
          class_id: Number(classId),
        }
        if (studentIdFilter) {
          payload.student_id = Number(studentIdFilter)
        }

        const res = await ApiEndpoints.getAllAttendance(payload)
        const attendanceForDate = (res.data?.attendance || []).filter(
          (att) => normalizeApiDate(att.date) === date,
        )

        let sourceStudents = studentList
        if (studentIdFilter) {
          sourceStudents = studentList.filter(
            (student) => Number(student.id) === Number(studentIdFilter),
          )
        }

        const merged = mergeStudentsWithAttendance(
          sourceStudents,
          attendanceForDate,
        )
        setTableMessage('')
        setData(sortStudentsByRollNumber(merged))
      } catch (error) {
        console.error(
          'Failed to load attendance for date:',
          error.response?.data || error.message,
        )
        let fallback = studentList.map((student) => mapStudentToRow(student))
        if (studentIdFilter) {
          fallback = fallback.filter(
            (student) => Number(student.id) === Number(studentIdFilter),
          )
        }
        setData(sortStudentsByRollNumber(fallback))
      } finally {
        setLoadingAttendance(false)
      }
    },
    [mergeStudentsWithAttendance, isHolidayForStudentAttendance],
  )

  useEffect(() => {
    if (!selectedDate) return

    // Show holiday message as soon as the date is known (including initial load),
    // even before a class/students are selected.
    if (!selectedClass || !classStudents.length) {
      let cancelled = false
      ;(async () => {
        const isHoliday = await isHolidayForStudentAttendance(selectedDate)
        if (cancelled) return
        if (isHoliday) {
          setData([])
          setOpenTable(true)
        }
      })()
      return () => {
        cancelled = true
      }
    }

    loadAttendanceForDate(
      classStudents,
      selectedClass,
      selectedDate,
      selectedStudent,
    )
  }, [
    selectedClass,
    selectedDate,
    classStudents,
    selectedStudent,
    loadAttendanceForDate,
    isHolidayForStudentAttendance,
  ])

  const handleSaveAttendance = async () => {
    try {
      if (!selectedDate) {
        toast.error('Please select an attendance date.')
        return
      }

      if (!selectedClass) {
        toast.error('Please select a class.')
        return
      }

      const isHoliday = await isHolidayForStudentAttendance(selectedDate)
      if (isHoliday) {
        setData([])
        setOpenTable(true)
        return
      }

      const attendanceDate = selectedDate
      const alreadyTakenRows = data.filter((student) => student.alreadyTaken)
      const newRows = data.filter(
        (student) =>
          !student.alreadyTaken &&
          ALLOWED_STATUSES.includes(student.status),
      )

      if (data.length > 0 && alreadyTakenRows.length === data.length) {
        toast.error(
          `Attendance already taken for ${formatDisplayDate(attendanceDate)}.`,
        )
        return
      }

      if (newRows.length === 0) {
        toast.error('Please select status for at least one unmarked student.')
        return
      }

      const attendanceData = newRows.map((student) => ({
        student_id: Number(student.id),
        status: student.status,
        remarks: student.remarks || '',
        date: attendanceDate,
      }))

      const payload = {
        class_id: Number(selectedClass),
        date: attendanceDate,
        attendance: attendanceData,
      }
      console.log('📌 Sending Payload:', JSON.stringify(payload, null, 2))

      setSaving(true)
      const response = await ApiEndpoints.addAttendance(payload)
      console.log('Response send:', response)
      toast.success(response.data.message || 'Attendance saved successfully.')

      await loadAttendanceForDate(
        classStudents,
        selectedClass,
        selectedDate,
        selectedStudent,
      )
    } catch (error) {
      console.error('Error Response:', error.response?.data || error.message)
      const apiMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.date?.[0] ||
        error.response?.data?.errors?.attendance?.[0]

      const lowerMessage = String(apiMessage || '').toLowerCase()
      if (
        lowerMessage.includes('already') ||
        lowerMessage.includes('exist') ||
        lowerMessage.includes('taken') ||
        lowerMessage.includes('duplicate')
      ) {
        toast.error(
          apiMessage ||
            `Attendance already taken for ${formatDisplayDate(selectedDate)}.`,
        )
        await loadAttendanceForDate(
          classStudents,
          selectedClass,
          selectedDate,
          selectedStudent,
        )
      } else {
        toast.error(apiMessage || 'Failed to save attendance.')
      }
    } finally {
      setSaving(false)
    }
  }

  const [selectedData, setSelectedData] = useState({})

  const handleSelect = (selectedValues) => {
    setSelectedData(selectedValues)

    if (selectedValues.class) {
      const classId = selectedValues.class
      setSelectedClass(classId)
      setOpenTable(true)
      const filtered = students.filter(
        (student) =>
          student.class_id != null &&
          classId != null &&
          student.class_id.toString() === classId.toString(),
      )
      console.log('filtered', filtered)
      setFilteredStudents(filtered)
      setClassStudents(filtered)
    }
  }

  const fetchFilteredData = ({ course, class: classId, student }) => {
    let updatedData = students
    console.log('COURSE:', course)
    console.log('class:', classId)
    console.log('student:', student)

    if (course) {
      updatedData = updatedData.filter(
        (item) => item.course_id?.toString() === course.toString(),
      )
      console.log('courseId', course)
    }

    if (classId) {
      updatedData = updatedData.filter(
        (item) => item.class_id?.toString() === classId.toString(),
      )
      console.log('classId', classId)
    }

    // if (student) {
    //   updatedData = updatedData.filter(
    //     (item) => item?.name?.toLowerCase().includes(student.toLowerCase()),
    //     console.log('student', student),
    //   )
    // }
    if (student) {
      console.log('STUDENT AFTER SELECT:', student)
      const studentName = students.find((s) => s.id === Number(student))?.name
      console.log('STUDENT NAME:', studentName?.name)
      if (studentName) {
        const updatedData = students.filter((item) =>
          item.name?.toLowerCase().includes(studentName.toLowerCase()),
        )
        setData(updatedData)
      }
    }

    // Update the state with the filtered data
    setFilteredStudents(updatedData)
  }

  useEffect(() => {
    fetchFilteredData(selectedData)
  }, [selectedData])

  const handleRefresh = () => {
    setSelectedData({})
    setSelectedCourseId('')
    setSelectedClass('')
    setSelectedStudentId(null)
    setSelectedStudent(null)
    setSelectedDate(getLocalDateString())
    setClasses([])
    setFilteredStudents([])
    setClassStudents([])
    setStudents([])
    setData([])
    setTableMessage('')
    setOpenTable(false)
    setCurrentPage(1)
    setResetKey((prev) => prev + 1)
  }

  // Pagination Logic
  const totalPages = Math.ceil(data.length / itemsPerPage)
  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [data, totalPages])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (course_id) {
      handleCourseChange({ target: { value: course_id } })
    }
  }, [course_id])

  const handleCourseChange = async (e) => {
    const courseId = e.target.value
    setSelectedCourseId(courseId)
    setClasses([])
    setFilteredStudents([])
    setSelectedData([])
    // setSelectedStudentId(null)
    // setSelectedClassId(null)
    // setSelectedStudent(null)

    console.log('courseId ', courseId, 'and', 'course_id', course_id)
    setOpenTable(true)
    if (courseId) {
      try {
        const res = await ApiEndpoints.getAllClassesByCourseId(courseId)
        const { data } = res
        if (data?.status === false || !data?.data?.length) {
          toast.error(data?.message || 'No classes found for this course')
          setClasses([])
          setOpenTable(false)
        } else {
          setClasses(data.data)
        }
      } catch (error) {
        const message = error?.response?.data?.message
        if (message === 'No classes found for this course') {
          toast.error(message)
        } else {
          console.error('Error fetching classes:', error)
          toast.error('Something went wrong while fetching classes.')
        }
        setClasses([])
        setOpenTable(false)
      }
    }
  }
  const handleClassChange = (e) => {
    const classId = e.target.value
    console.log(' Targeted classId', classId)
    setSelectedClass(classId)
    setSelectedStudent(null)
    setSelectedStudentId(null)
    setData([])
    setTableMessage('')
    setClassStudents([])
    setOpenTable(Boolean(classId))

    if (classId) {
      const institueId = Cookies.get('institute_id')
      const headers = {
        'institute-Id': institueId || institute_id,
        Authorization: `Bearer ${token}`
      }
      console.log('HEADERS:', headers)
      axios
        .get(
          BASE_URL+`/get/student/by-class?class_id=${classId}`,
          { headers },
        )
        .then(async (res) => {
          console.log('Students after class select:', res.data.data)
          const studentList = res.data.data || []
          const enrichedStudents = await enrichStudentsWithRollNumbers(
            studentList,
            classId,
            token,
          )
          setStudents(enrichedStudents)
          setClassStudents(enrichedStudents)
          setFilteredStudents(enrichedStudents)
        })
        .catch((error) => {
          if (
            error.response?.data?.message === 'No classes found for this course'
          ) {
            console.error('No classes found for this course.')
          } else {
            console.error('Error fetching classes:', error)
            console.error('Something went wrong while fetching classes.')
          }
          setStudents([])
          setClassStudents([])
          setData([])
        })
    }
  }
  const handleStudentChange = async (e) => {
    const studentId = e.target.value
    setSelectedStudent(studentId || null)
    setSelectedStudentId(studentId || null)
    setCurrentPage(1)
    setOpenTable(true)
  }

  const alreadyTakenCount = data.filter((student) => student.alreadyTaken).length
  const allAlreadyTaken = data.length > 0 && alreadyTakenCount === data.length
  const isBusy = loadingAttendance || saving || checkingHoliday

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%',
            }}
          >
            {/* Left: Title */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
              }}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Take Attendance
              </h3>
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Filter Toggle Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          {/* Modern Filter Section */}
          <div 
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Course, Class & Student
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Choose course, class, and student to take attendance
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff', overflow: 'visible', position: 'relative' }}>
                  <Row className="g-3 text-start">
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Course
                        </label>
                        <Typeahead
                          id="course-select"
                          labelKey="name"
                          onChange={(selected) => {
                            const course = selected[0]
                            if (course) {
                              handleCourseChange({ target: { value: course.id } })
                            } else {
                              handleCourseChange({ target: { value: '' } })
                            }
                          }}
                          options={courses}
                          placeholder="Select Course"
                          selected={courses.filter(
                            (cs) => cs.id === Number(selectedCourseId || course_id || ''),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Class
                        </label>
                        <Typeahead
                          id="class-select"
                          labelKey="class_name"
                          onChange={(selected) => {
                            const cls = selected[0]
                            if (cls) {
                              handleClassChange({ target: { value: cls.id } })
                            } else {
                              handleClassChange({ target: { value: '' } })
                            }
                          }}
                          options={classes}
                          placeholder="Select Class"
                          selected={classes.filter(
                            (cs) => cs.id === Number(selectedClass || class_id || ''),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Student
                        </label>
                        <Typeahead
                          id="student-select"
                          labelKey={(option) =>
                            `${option.name} - ${option.admission_number}`
                          }
                          onChange={(selected) => {
                            const student = selected[0]
                            if (student) {
                              handleStudentChange({ target: { value: student.id } })
                            } else {
                              handleStudentChange({ target: { value: '' } })
                            }
                          }}
                          options={students}
                          placeholder="Select Student"
                          selected={
                            Array.isArray(students)
                              ? students.filter(
                                  (s) => s.id === Number(selectedStudent || ''),
                                )
                              : []
                          }
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Items Per Page Selector & Date Selection */}
          {(openTable || Boolean(tableMessage)) && (
            <div
              className="d-flex mb-4"
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: isMobile ? 'flex-start' : 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? '12px' : '0',
              }}
            >
              <div
                className="d-flex align-items-center gap-3"
                style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
              >
                <div 
                  className="d-flex align-items-center"
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    gap: '8px',
                    border: '1px solid #D1D3D4',
                  }}
                >
                  <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Show:
                  </span>
                  <Form.Select
                    className="w-auto"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value))
                      setCurrentPage(1)
                    }}
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #D1D3D4',
                      backgroundColor: '#ffffff',
                      color: '#111827',
                      fontSize: '13px',
                      fontWeight: '500',
                      padding: '4px 32px 4px 12px',
                      minWidth: '140px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      height: '36px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#223843'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D3D4'
                    }}
                  >
                    <option value="10">10 per page</option>
                    <option value="20">20 per page</option>
                    <option value="30">30 per page</option>
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div style={{ position: 'relative' }}>
                    <BsCalendar3 
                      style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#6b7280',
                        fontSize: '16px',
                        zIndex: 1,
                        pointerEvents: 'none'
                      }} 
                    />
                    <Form.Control
                      type="date"
                      value={selectedDate || getLocalDateString()}
                      onChange={(e) =>
                        setSelectedDate(e.target.value || getLocalDateString())
                      }
                      disabled={isBusy}
                      title="Select past, current, or future date"
                      style={{
                        paddingLeft: '40px',
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4',
                        fontSize: '14px',
                        height: '40px',
                        transition: 'all 0.2s ease',
                        backgroundColor: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        width: '180px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#223843'
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#D1D3D4'
                      }}
                    />
                  </div>
                </div>
              </div>
              {data.length > 0 && !allAlreadyTaken && !checkingHoliday && (
                <div
                  className="d-flex gap-3 flex-wrap"
                  style={{
                    justifyContent: isMobile ? 'stretch' : 'flex-end',
                    alignItems: 'center',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  {/* Mark All Present Button */}
                  {isMobile ? (
                    <button
                      disabled={isBusy}
                      onClick={() => {
                        const updated = data.map((student) =>
                          student.alreadyTaken
                            ? student
                            : { ...student, status: 'present' },
                        )
                        setData(updated)
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '44px',
                        borderRadius: '8px',
                        border: '1px solid #10B981',
                        backgroundColor: '#10B981',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isBusy ? 0.6 : 1,
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        padding: '0 12px',
                      }}
                      title="Mark All Present"
                    >
                      <BsCheckCircleFill size={18} />
                      Mark All Present
                    </button>
                  ) : (
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{
                        transition: 'all 0.2s ease',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (isBusy) return
                        const button = e.currentTarget.querySelector('button')
                        const text = e.currentTarget.querySelector('span')
                        if (button) {
                          button.style.backgroundColor = '#059669'
                          button.style.borderColor = '#059669'
                          button.style.transform = 'scale(1.05)'
                        }
                        if (text) {
                          text.style.transform = 'scale(1.05)'
                        }
                        e.currentTarget.style.transform = 'scale(1.02)'
                      }}
                      onMouseLeave={(e) => {
                        const button = e.currentTarget.querySelector('button')
                        const text = e.currentTarget.querySelector('span')
                        if (button) {
                          button.style.backgroundColor = '#10B981'
                          button.style.borderColor = '#10B981'
                          button.style.transform = 'scale(1)'
                        }
                        if (text) {
                          text.style.transform = 'scale(1)'
                        }
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                    >
                      <button
                        disabled={isBusy}
                        onClick={() => {
                          const updated = data.map((student) =>
                            student.alreadyTaken
                              ? student
                              : { ...student, status: 'present' },
                          )
                          setData(updated)
                        }}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: '1px solid #10B981',
                          backgroundColor: '#10B981',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isBusy ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                        }}
                        title="Mark All Present"
                      >
                        <BsCheckCircleFill size={20} />
                      </button>
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#111827', 
                        fontFamily: 'Inter, system-ui, sans-serif', 
                        fontWeight: '500',
                        transition: 'transform 0.2s ease',
                        display: 'inline-block',
                      }}>
                        Mark All Present
                      </span>
                    </div>
                  )}
                  
                  {/* Mark All Absent Button */}
                  {isMobile ? (
                    <button
                      disabled={isBusy}
                      onClick={() => {
                        const updated = data.map((student) =>
                          student.alreadyTaken
                            ? student
                            : { ...student, status: 'absent' },
                        )
                        setData(updated)
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '44px',
                        borderRadius: '8px',
                        border: '1px solid #EF4444',
                        backgroundColor: '#EF4444',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isBusy ? 0.6 : 1,
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        padding: '0 12px',
                      }}
                      title="Mark All Absent"
                    >
                      <BsXCircleFill size={18} />
                      Mark All Absent
                    </button>
                  ) : (
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{
                        transition: 'all 0.2s ease',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (isBusy) return
                        const button = e.currentTarget.querySelector('button')
                        const text = e.currentTarget.querySelector('span')
                        if (button) {
                          button.style.backgroundColor = '#DC2626'
                          button.style.borderColor = '#DC2626'
                          button.style.transform = 'scale(1.05)'
                        }
                        if (text) {
                          text.style.transform = 'scale(1.05)'
                        }
                        e.currentTarget.style.transform = 'scale(1.02)'
                      }}
                      onMouseLeave={(e) => {
                        const button = e.currentTarget.querySelector('button')
                        const text = e.currentTarget.querySelector('span')
                        if (button) {
                          button.style.backgroundColor = '#EF4444'
                          button.style.borderColor = '#EF4444'
                          button.style.transform = 'scale(1)'
                        }
                        if (text) {
                          text.style.transform = 'scale(1)'
                        }
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                    >
                      <button
                        disabled={isBusy}
                        onClick={() => {
                          const updated = data.map((student) =>
                            student.alreadyTaken
                              ? student
                              : { ...student, status: 'absent' },
                          )
                          setData(updated)
                        }}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: '1px solid #EF4444',
                          backgroundColor: '#EF4444',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isBusy ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                        }}
                        title="Mark All Absent"
                      >
                        <BsXCircleFill size={20} />
                      </button>
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#111827', 
                        fontFamily: 'Inter, system-ui, sans-serif', 
                        fontWeight: '500',
                        transition: 'transform 0.2s ease',
                        display: 'inline-block',
                      }}>
                        Mark All Absent
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {((openTable || Boolean(tableMessage)) &&
            (loadingAttendance || checkingHoliday)) && (
            <div className="d-flex align-items-center gap-2 mb-3" style={{ color: '#6b7280' }}>
              <Spinner animation="border" size="sm" />
              <span style={{ fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                {checkingHoliday
                  ? 'Checking holiday for selected date...'
                  : 'Checking attendance for selected date...'}
              </span>
            </div>
          )}

          {/* Table */}
          {openTable && data?.length > 0 && !checkingHoliday ? (
            <>
              <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Number</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Roll Number</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Student Name</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Father Name</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Status</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Remarks</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentItems.map((item, index) => (
                      <Tr 
                        key={index}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          transition: 'background-color 0.2s ease',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                        }}
                      >
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.admission_number}</Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{pickRollNumber(item.roll_number, item.roll_no, item.rollNumber) || '-'}</Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.name}</Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.father_name || '-'}</Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          <Form.Select
                            value={item.status || ''}
                            onChange={(e) =>
                              handleStatusChange(item.id, e.target.value)
                            }
                            disabled={item.alreadyTaken || isBusy}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              backgroundColor: item.alreadyTaken ? '#F3F4F6' : '#ffffff',
                            }}
                          >
                            <option value="">Select</option>
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                            <option value="leave">Leave</option>
                          </Form.Select>
                          {item.alreadyTaken && (
                            <div style={{ fontSize: '11px', color: '#92400E', marginTop: '4px' }}>
                              Already marked
                            </div>
                          )}
                        </Td>
                        <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          <Form.Control
                            type="text"
                            placeholder="Enter remarks"
                            value={item.remarks || ''}
                            onChange={(e) =>
                              handleRemarksChange(item.id, e.target.value)
                            }
                            disabled={
                              item.alreadyTaken ||
                              isBusy ||
                              (item.status !== 'present' && item.status !== 'leave')
                            }
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              backgroundColor: item.alreadyTaken ? '#F3F4F6' : '#ffffff',
                            }}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Card>

              <TablePagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                totalItems={data.length}
                itemLabel="students"
                maxVisiblePages={isMobile ? 5 : 7}
                paginationScrollable
              />

              <div className="d-flex flex-wrap justify-content-start align-items-center mb-5 mt-4 gap-2">
                {/* Save Attendance Button */}
                <div
                  className="d-flex align-items-center gap-2"
                  style={{
                    transition: 'all 0.2s ease',
                    cursor: allAlreadyTaken || isBusy ? 'not-allowed' : 'pointer',
                    opacity: allAlreadyTaken || isBusy ? 0.55 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (allAlreadyTaken || isBusy) return
                    const button = e.currentTarget.querySelector('button')
                    const text = e.currentTarget.querySelector('span')
                    if (button) {
                      button.style.backgroundColor = '#059669'
                      button.style.borderColor = '#059669'
                      button.style.transform = 'scale(1.05)'
                    }
                    if (text) {
                      text.style.transform = 'scale(1.05)'
                    }
                    e.currentTarget.style.transform = 'scale(1.02)'
                  }}
                  onMouseLeave={(e) => {
                    const button = e.currentTarget.querySelector('button')
                    const text = e.currentTarget.querySelector('span')
                    if (button) {
                      button.style.backgroundColor = '#10B981'
                      button.style.borderColor = '#10B981'
                      button.style.transform = 'scale(1)'
                    }
                    if (text) {
                      text.style.transform = 'scale(1)'
                    }
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                >
                  <button
                    onClick={handleSaveAttendance}
                    disabled={allAlreadyTaken || isBusy}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      border: '1px solid #10B981',
                      backgroundColor: '#10B981',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: allAlreadyTaken || isBusy ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                    }}
                    title={
                      allAlreadyTaken
                        ? 'Attendance already taken for selected date'
                        : 'Save Attendance'
                    }
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : <BsCheckLg size={20} />}
                  </button>
                  {!isMobile && (
                    <span style={{ 
                      fontSize: '14px', 
                      color: '#111827', 
                      fontFamily: 'Inter, system-ui, sans-serif', 
                      fontWeight: '500',
                      transition: 'transform 0.2s ease',
                      display: 'inline-block',
                    }}>
                      {allAlreadyTaken
                        ? 'Already Taken'
                        : saving
                          ? 'Saving...'
                          : 'Save Attendance'}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (openTable || tableMessage) &&
            !loadingAttendance &&
            !checkingHoliday ? (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px', backgroundColor: '#ffffff' }}>
              <Card.Body>
                <div className="text-center py-5">
                  <p
                    style={{
                      color: tableMessage ? '#B91C1C' : '#6b7280',
                      fontSize: '14px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontWeight: tableMessage ? '600' : '400',
                    }}
                  >
                    {tableMessage || 'No students found.'}
                  </p>
                </div>
              </Card.Body>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default DataTable
